<template>
    <div class="section contact-section fade-in-section">
        <h2 class="section-title">Contact Us</h2>

        <div class="contact-content">
            <div class="contact-info">
                <div class="contact-item">
                    <Mail :size="24" class="icon" />
                    <div>
                        <h4>Email</h4>
                        <a :href="'mailto:' + config.contact.email">{{ config.contact.email }}</a>
                    </div>
                </div>

                <div class="contact-item">
                    <MapPin :size="24" class="icon" />
                    <div>
                        <h4>Location</h4>
                        <p>{{ config.contact.location }}</p>
                    </div>
                </div>
            </div>

            <div class="contact-form">
                <form @submit.prevent="handleSubmit">
                    <div class="form-group">
                        <input
                            type="text"
                            v-model="form.name"
                            placeholder="Your Name"
                            :disabled="isLoggedIn"
                            required
                        />
                    </div>
                    <div class="form-group">
                        <input
                            type="email"
                            v-model="form.email"
                            placeholder="Your Email"
                            :disabled="isLoggedIn"
                            required
                        />
                    </div>
                    <div class="form-group">
                        <textarea
                            v-model="form.message"
                            placeholder="Your Message"
                            rows="5"
                            required
                        ></textarea>
                    </div>
                    <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                        <span v-if="!isSubmitting">Send Message</span>
                        <span v-else>Sending...</span>
                    </button>
                </form>

                <div v-if="showSuccess" class="success-message">
                    <CheckCircle :size="20" />
                    <span>Thank you! Your message has been sent.</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Mail, MapPin, CheckCircle } from 'lucide-vue-next';
import { SITE_CONFIG } from '@/config/site';
import { useAuthStore } from '@/stores/auth';

export default {
    name: 'ContactSection',
    components: {
        Mail,
        MapPin,
        CheckCircle
    },
    data() {
        const authStore = useAuthStore();

        return {
            config: SITE_CONFIG,
            authStore,
            form: {
                name: authStore.user?.full_name || '',
                email: authStore.user?.email || '',
                message: ''
            },
            isSubmitting: false,
            showSuccess: false
        };
    },
    computed: {
        isLoggedIn() {
            return this.authStore.isAuthenticated;
        }
    },
    methods: {
        async handleSubmit() {
            this.isSubmitting = true;

            // Simulate form submission (replace with actual API call later)
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Reset form and show success
            // Keep name and email if user is logged in
            this.form = {
                name: this.authStore.user?.full_name || '',
                email: this.authStore.user?.email || '',
                message: ''
            };
            this.isSubmitting = false;
            this.showSuccess = true;

            // Hide success message after 5 seconds
            setTimeout(() => {
                this.showSuccess = false;
            }, 5000);
        }
    }
};
</script>

<style scoped>
.section {
    position: relative;
    z-index: 2;
    padding: 80px 20px;
    max-width: 80vw;
    margin: 0 auto;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-bottom: 1px solid var(--accent);
}

.section-title {
    font-size: 42px;
    color: var(--text-secondary);
    text-align: center;
    margin-bottom: 60px;
}

.contact-content {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: 60px;
    align-items: start;
}

.contact-info {
    display: flex;
    flex-direction: column;
    gap: 30px;
}

.contact-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.contact-item .icon {
    color: var(--accent);
    flex-shrink: 0;
    margin-top: 4px;
}

.contact-item h4 {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.contact-item a,
.contact-item p {
    font-size: 16px;
    color: var(--text-secondary);
    text-decoration: none;
    transition: color 0.2s ease;
}

.contact-item a:hover {
    color: var(--accent);
}

.contact-form {
    background: rgba(var(--bg-secondary-rgb), 0.95);
    padding: 40px;
    border: 1px solid rgba(var(--accent-rgb), 0.4);
}

.form-group {
    margin-bottom: 20px;
}

.form-group input,
.form-group textarea {
    width: 100%;
    padding: 14px 16px;
    background: var(--bg-primary);
    border: 1px solid rgba(var(--accent-rgb), 0.3);
    color: var(--text-primary);
    font-size: 15px;
    font-family: inherit;
    transition: border-color 0.2s ease;
}

.form-group input:disabled {
    background: rgba(var(--accent-rgb), 0.1);
    opacity: 0.8;
    cursor: not-allowed;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
}

.form-group input:focus,
.form-group textarea:focus {
    outline: none;
    border-color: var(--accent);
}

.form-group textarea {
    resize: vertical;
    min-height: 120px;
}

.btn {
    padding: 15px 40px;
    font-weight: 600;
    font-size: 16px;
    border: none;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-primary {
    background: var(--accent);
    color: var(--bg-primary);
}

.btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
}

.btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.success-message {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
    padding: 14px 20px;
    background: rgba(76, 175, 80, 0.15);
    border: 1px solid rgba(76, 175, 80, 0.4);
    border-radius: 6px;
    color: #4caf50;
    font-size: 14px;
}

/* Fade-in animation */
.fade-in-section {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-section.is-visible {
    opacity: 1;
    transform: translateY(0);
}

@media (max-width: 768px) {
    .section {
        min-height: auto;
        padding: 60px 20px;
    }

    .section-title {
        font-size: 32px;
        margin-bottom: 40px;
    }

    .contact-content {
        grid-template-columns: 1fr;
        gap: 40px;
    }

    .contact-form {
        padding: 30px 20px;
    }
}
</style>
